import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown, ShieldCheck, Droplets, Factory, Timer } from "lucide-react";
import { waMessage, DEFAULT_WA_MSG } from "@/data";
import { scrollToSection } from "@/components/Navbar";

const lines = [
  { text: "QUALITY LUBRICANTS", cls: "text-white" },
  { text: "FOR A STRONGER", cls: "text-gold" },
  { text: "TOMORROW", cls: "text-gold" },
];

const stats = [
  { icon: Droplets, label: "100% Virgin Spec Base Oils" },
  { icon: ShieldCheck, label: "API & ACEA Standards" },
  { icon: Factory, label: "Registered ® LOBP" },
  { icon: Timer, label: "15+ Years Supplying" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden grain"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[36rem] h-[36rem] rounded-full bg-gold/10 blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[30rem] h-[30rem] rounded-full bg-gold/[0.07] blur-[120px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(229,169,60,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(229,169,60,0.04)_1px,transparent_1px)] bg-[size:72px_72px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-36 pb-24 grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center w-full">
        <motion.div style={{ opacity: fade }}>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.35em] text-gold mb-7"
            data-testid="hero-eyebrow"
          >
            Auto City Lubricants (PTY) Ltd — Registered ® LOBP
          </motion.p>

          <h1
            className="font-heading font-black uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-[5.4rem]"
            data-testid="hero-headline"
          >
            {lines.map((l, i) => (
              <span key={l.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${l.cls}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed"
            data-testid="hero-subtext"
          >
            South Africa's fastest growing locally blended oil brand. Bulk supply of engine,
            hydraulic, gear and transmission oils, antifreeze and grease — blended in Benoni to API
            &amp; ACEA standards that meet or exceed OEM approvals.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-cta-whatsapp"
              href={waMessage(DEFAULT_WA_MSG)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-3 bg-gold text-obsidian font-extrabold uppercase tracking-widest text-sm px-8 py-4 rounded-full hover:bg-gold-bright hover:shadow-[0_0_40px_rgba(229,169,60,0.5)] hover:-translate-y-0.5 transition-all duration-300"
            >
              <MessageCircle size={18} strokeWidth={2.5} /> WhatsApp Us
            </a>
            <a
              data-testid="hero-cta-products"
              href="#products"
              onClick={(e) => scrollToSection(e, "#products")}
              className="group inline-flex items-center gap-3 border border-gold/40 text-gold font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-full hover:bg-gold/10 hover:border-gold transition-all duration-300"
            >
              Explore Products{" "}
              <ArrowDown size={16} className="group-hover:translate-y-0.5 transition-transform duration-300" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6"
            data-testid="hero-stats"
          >
            {stats.map((s) => (
              <div key={s.label} className="flex flex-col gap-2">
                <s.icon size={20} className="text-gold" strokeWidth={1.75} />
                <span className="text-[11px] uppercase tracking-widest text-zinc-400 leading-snug">
                  {s.label}
                </span>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:block"
          data-testid="hero-poster-frame"
        >
          <div className="absolute -inset-6 bg-gold/15 blur-3xl rounded-full" />
          <motion.div
            style={{ y: imgY }}
            className="relative rounded-2xl overflow-hidden border border-gold/25 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]"
          >
            <img
              src="/assets/gen-hero.png"
              alt="Auto City Lubricants — Quality Lubricants For A Stronger Tomorrow"
              className="w-full h-[34rem] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 via-transparent to-transparent" />
          </motion.div>
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -left-8 top-10 bg-coal/90 backdrop-blur-md border border-gold/30 rounded-xl px-5 py-4"
          >
            <p className="font-heading font-extrabold text-gold text-xl uppercase leading-none">210L Drums</p>
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">Bulk Supply Ready</p>
          </motion.div>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute -right-6 bottom-12 bg-coal/90 backdrop-blur-md border border-gold/30 rounded-xl px-5 py-4"
          >
            <p className="font-heading font-extrabold text-white text-xl uppercase leading-none">API SL / CI-4</p>
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">OEM Approved</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
